/**
 * ControlPanel API (`/device-registrations`) クライアント (SBI-5)。
 *
 * - すべての呼び出しに `Authorization: Bearer <Cognito ID Token>` を付与する。
 * - エンドポイントは `AppConfig.apiUrl` をベースとする。
 * - 端末ID⇄編成番号の紐づけ + 動作ステータスの一覧を取得する。
 * - 端末の再起動要求 (C-3 / C-5) も本クライアント経由で行う。
 */
import type { AppConfig } from '../config';

import { ApiError, joinUrl } from './broadcastPoints';
import type {
  ApiErrorBody,
  DeviceRebootResponse,
  DeviceRegistrationBulkDeleteResponse,
  DeviceRegistrationSaveResult,
  DeviceRegistrationListResponse,
  DeviceRegistrationUpdateRequest,
} from './types';

export interface DeviceRegistrationsClientOptions {
  /** Cognito ID トークンを取得する関数 (期限切れ時は AuthProvider 側で自動更新済み) */
  getIdToken: () => Promise<string>;
  /** AppConfig (apiUrl 取得用) */
  config: AppConfig;
  /** テスト時に差し込む fetch 実装 */
  fetchImpl?: typeof fetch;
}

export class DeviceRegistrationsClient {
  private readonly options: DeviceRegistrationsClientOptions;

  constructor(options: DeviceRegistrationsClientOptions) {
    this.options = options;
  }

  /**
   * GET /device-registrations
   *
   * 端末ID⇄編成番号の最新紐づけ + 動作ステータスの一覧を取得する。
   * 現在は全件取得のみ対応（ページング機能は将来実装予定）。
   *
   * @throws Error - 204 No Content が返された場合（エラーとして扱う）
   */
  async list(): Promise<DeviceRegistrationListResponse> {
    const result = await this.request<DeviceRegistrationListResponse | undefined>(
      'GET',
      'device-registrations',
    );
    if (result === undefined) {
      throw new Error('Unexpected 204 No Content from GET /device-registrations');
    }
    return result;
  }

  /**
   * PUT /device-registrations/{vehicle_id}
   *
   * 端末名 (`device_name`) とデバイスID (`airdroid_device_id`) を **常に 2 項目とも**
   * 送って保存する (Issue #360 / C-5 / Issue #885)。
   *
   * サーバ側の PUT は「編集対象 2 項目の全置換」で、キーが無い項目は解除される。
   * 片方だけを送るともう片方が消えるため、行単位の保存では必ず両方を送る。
   * 空文字を渡した項目は解除される (端末名は属性削除 / デバイスIDは DeviceIdMapping の削除)。
   *
   * デバイスIDの保存先は DeviceRegistry ではなく **DeviceIdMapping (PK=`device_id`)** (S-2)。
   * 応答には DeviceIdMapping 由来の合成値として `airdroid_device_id` が必ず含まれるため、
   * 呼び出し側は応答をそのまま表示に反映してよい。
   *
   * `vehicle_id` は `#` を含む合成キー (例 `15#014`) なので、必ず
   * `encodeURIComponent` でエスケープしてからパスに埋め込む。
   * (エスケープしないと `#` 以降がフラグメントとして落ち、別編成を更新したり
   *  404 になるという分かりにくい不具合になる)
   *
   * 主なエラー: 400 (文字種の違反 / デバイスID指定時に当該編成へ `device_id` が未登録) /
   * 404 (編成レコード無し)。
   *
   * @throws Error - 204 No Content が返された場合（エラーとして扱う）
   */
  async updateDevice(
    vehicleId: string,
    values: { deviceName: string; airdroidDeviceId: string },
  ): Promise<DeviceRegistrationSaveResult> {
    return this.updateRegistration(vehicleId, {
      device_name: values.deviceName,
      airdroid_device_id: values.airdroidDeviceId,
    });
  }

  /**
   * POST /device-registrations/{vehicle_id}/reboot (C-3 / C-5)
   *
   * 当該編成の端末を **今すぐ再起動** するよう要求する。夜間定時再起動と同じ
   * RebootWorker を同期 invoke するため、結果記録 (`last_reboot_*`) とメトリクスは
   * 夜間バッチと同じ経路を通る。
   *
   * `vehicle_id` は `#` を含む合成キー (例 `15#014`) なので、必ず
   * `encodeURIComponent` でエスケープしてからパスに埋め込む。
   * (エスケープしないと `#` 以降がフラグメントとして落ち、**別編成を再起動する**
   *  という取り返しのつかない事故につながる)
   *
   * ⚠️ 200 応答は「再起動要求の経路が成立した」ことしか意味しない。実際に AirDroid
   * へ HTTP を発行したかは応答の `dry_run` で判別する (AirDroid 再起動 API の
   * リクエストフォーマットは疎通確認前 / docs/nightly-reboot.md §7 ①)。
   *
   * 主なエラー: 404 (編成レコード無し) / 409 (AirDroid 端末 ID 未登録) /
   * 502 (RebootWorker の invoke 失敗 or `ok: false`)。
   *
   * @throws Error - 204 No Content が返された場合（エラーとして扱う）
   */
  async rebootDevice(vehicleId: string): Promise<DeviceRebootResponse> {
    const result = await this.request<DeviceRebootResponse | undefined>(
      'POST',
      `device-registrations/${encodeURIComponent(vehicleId)}/reboot`,
    );
    if (result === undefined) {
      throw new Error(
        'Unexpected 204 No Content from POST /device-registrations/{vehicle_id}/reboot',
      );
    }
    return result;
  }

  /**
   * POST /device-registrations/bulk-delete (Issue #638)
   *
   * 端末管理画面でチェックを付けた編成をまとめて削除する。`vehicle_id` は `#` を含む
   * 合成キー (例 `15#014`) だが、JSON ボディで送るためエスケープは不要。
   *
   * 応答の `deleted` / `not_found` / `failed` で個別の結果を判別できる
   * (存在しない編成は冪等にエラーとせず `not_found` に入る)。
   *
   * @throws Error - `vehicleIds` が空の場合 / 204 No Content が返された場合
   */
  async bulkDelete(vehicleIds: string[]): Promise<DeviceRegistrationBulkDeleteResponse> {
    if (vehicleIds.length === 0) {
      throw new Error('vehicleIds must not be empty');
    }
    const result = await this.request<DeviceRegistrationBulkDeleteResponse | undefined>(
      'POST',
      'device-registrations/bulk-delete',
      { vehicle_ids: vehicleIds },
    );
    if (result === undefined) {
      throw new Error('Unexpected 204 No Content from POST /device-registrations/bulk-delete');
    }
    return result;
  }

  /**
   * PUT /device-registrations/{vehicle_id} の共通処理。
   *
   * 呼び出し側が指定したキーだけを送る。サーバ側の PUT は編集対象項目の全置換で、
   * 送らなかった項目は解除される点に注意 (詳細は
   * {@link DeviceRegistrationUpdateRequest} のコメント)。
   */
  private async updateRegistration(
    vehicleId: string,
    body: DeviceRegistrationUpdateRequest,
  ): Promise<DeviceRegistrationSaveResult> {
    const result = await this.request<DeviceRegistrationSaveResult | undefined>(
      'PUT',
      `device-registrations/${encodeURIComponent(vehicleId)}`,
      body,
    );
    if (result === undefined) {
      throw new Error('Unexpected 204 No Content from PUT /device-registrations/{vehicle_id}');
    }
    return result;
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = joinUrl(this.options.config.apiUrl, path);
    const idToken = await this.options.getIdToken();
    const fetchImpl = this.options.fetchImpl ?? fetch;

    const res = await fetchImpl(url, {
      method,
      headers: {
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${idToken}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let errorBody: ApiErrorBody | undefined;
      try {
        errorBody = (await res.json()) as ApiErrorBody;
      } catch {
        // body が JSON で無い場合は無視
      }
      const message = errorBody?.message ?? 'API リクエストに失敗しました';
      throw new ApiError(res.status, message, errorBody);
    }

    if (res.status === 204) {
      return undefined as T;
    }

    return (await res.json()) as T;
  }
}

/** 端末種別。`device_id` プレフィックスからフロント側で導出する。 */
export type DeviceType = 'Web' | 'Android' | '不明';

/**
 * `device_id` のプレフィックスから端末種別を導出する。
 *
 * - `web-` / `web` 始まり → Web
 * - `and-` / `android` 始まり → Android
 * - `ios-` は iOS 端末だが、現状の一覧では Web/Android の 2 分類のみを扱うため
 *   判定対象外 (不明) とする。
 * - それ以外 / 空 → 不明
 *
 * 判定は大文字小文字を区別しない。
 */
export function deviceTypeFromId(deviceId: string | null | undefined): DeviceType {
  if (!deviceId) return '不明';
  const id = deviceId.toLowerCase();
  if (id.startsWith('web-') || id.startsWith('web')) return 'Web';
  if (id.startsWith('and-') || id.startsWith('android')) return 'Android';
  return '不明';
}
